import type { DescribedRow } from './describedRows';
import type { FieldPath } from './types';

/**
 * Find the staged change belonging to a control the grid is about to render.
 *
 * The banner and the chat card both read `describedRows` — a list, in log
 * order, which is the right shape for a summary and the wrong one for a field
 * that needs to ask "is there a change on ME". This is the same rows, indexed
 * by where they live.
 *
 * Built from `describedRows` rather than from the projection directly, so the
 * value an underwriter reads on the field is the value the card reports for it
 * — one producer, one set of borrowed labels (spec D19). Deriving them twice is
 * how the grid and the card could come to disagree about what changed.
 */

/**
 * Match by PREFIX, not by whole path.
 *
 * A call site knows the field it is rendering, never the leaf beneath it: the
 * global retroactive period lands at `global.value.props.retroactive.props.
 * period.value` while its control only knows `retroactive`. A prefix asks the
 * question the caller can actually answer.
 *
 * Segment by segment. Never `join('.')` and compare strings — a collection key
 * can contain the separator (D17), so `coverageState.techEO.ZSP_DNO` is three
 * segments here and unparseable as text.
 */
function startsWith(path: readonly string[], prefix: FieldPath): boolean {
  return (
    prefix.length <= path.length && prefix.every((seg, i) => path[i] === seg)
  );
}

export interface StagedFieldIndex {
  /**
   * Every staged change at or below one of these prefixes, in log order.
   *
   * A list rather than a single hit, because one control can own more than one
   * changed path — the two date pickers share a header cell, and a nested
   * global writes several leaves. The caller renders what it is given instead
   * of a marker having to guess which one is "the" change.
   */
  readonly at: (prefixes: readonly FieldPath[]) => readonly DescribedRow[];
  /** Every row produced by one log entry. What accepting it would move. */
  readonly forEntry: (entryId: string) => readonly DescribedRow[];
}

const EMPTY: readonly DescribedRow[] = [];

/** Nothing staged. The value every consumer outside a `DraftProvider` reads. */
export const NO_STAGED_FIELDS: StagedFieldIndex = {
  at: () => EMPTY,
  forEntry: () => EMPTY,
};

export function buildStagedFieldIndex(
  rows: readonly DescribedRow[]
): StagedFieldIndex {
  if (rows.length === 0) {
    return NO_STAGED_FIELDS;
  }

  return {
    at: (prefixes) =>
      rows.filter((row) =>
        prefixes.some((prefix) => startsWith(row.path, prefix))
      ),
    forEntry: (entryId) => rows.filter((row) => row.entryId === entryId),
  };
}

/**
 * Where each control's changes live.
 *
 * Deliberately one segment short of the leaf: these are prefixes, and the leaf
 * differs by field shape (`…limit.value`, but `…retroactive.props.period.value`).
 * Centralised so a path shape is written once — a hand-built array at a call
 * site is a silent no-op when it is wrong, since an address that matches
 * nothing renders exactly like a field with no staged change.
 */
export const EFFECTIVE_DATE_PATH: FieldPath = ['effectiveDate'];
export const END_DATE_PATH: FieldPath = ['endDate'];

export const coverageFieldPath = (id: string, field: string): FieldPath => [
  'coverageState',
  id,
  'value',
  'props',
  field,
];

/**
 * Every staged prop on one coverage.
 *
 * For a control that owns more than one field, or whose fields depend on the
 * coverage — `AmendatoryCoverageLineItemFactory` renders a different editor per
 * amendatory coverage, each writing its own prop shape. Naming the field would
 * mean re-deriving that choice out here, and a wrong guess renders as no marker
 * at all.
 */
export const coveragePropsPath = (id: string): FieldPath => [
  'coverageState',
  id,
  'value',
  'props',
];

/**
 * Everything one coverage row can stage a change at.
 *
 * The row is the unit of the treatment — it tints as a whole, its Premium
 * strikes through as a whole, and it carries one accept/reject pair — so those
 * three all have to ask the same question, and they ask it through this.
 */
export const coverageRowPaths = (id: string): readonly FieldPath[] => [
  coveragePropsPath(id),
  coverageInclusionPath(id),
  coverageReferralStrategyPath(id),
];

/**
 * The coverage-state keys behind one multi-rater endorsement row.
 *
 * `EndorsementLineItem` renders one row per endorsement with a checkbox per
 * suite part, and each part is its own coverage, filed under
 * `${endorsementId}.${part}`.
 */
export const endorsementPartKeys = (
  id: string,
  components: readonly string[]
): readonly string[] => components.map((component) => `${id}.${component}`);

/**
 * Everything one multi-rater endorsement row can stage a change at: every
 * part's props, inclusion and referral strategy. The row's one accept/reject
 * pair asks through this.
 */
export const endorsementRowPaths = (
  id: string,
  components: readonly string[]
): readonly FieldPath[] =>
  endorsementPartKeys(id, components).flatMap((key) => coverageRowPaths(key));

/**
 * Which part's inclusion the row's `Excluded -> Included` mark describes.
 *
 * `StagedTransition` draws each change at the same spot under the name, so it
 * can describe one direction. When several parts are staged and all move the
 * same way, one mark says it truthfully; when they move different ways there is
 * no single direction to write, and the mark is left off rather than show one
 * part's change as the row's. The accept/reject pair still covers every part.
 */
export function endorsementTransitionPaths(
  id: string,
  components: readonly string[],
  stagedFields: StagedFieldIndex
): readonly FieldPath[] {
  const staged = endorsementPartKeys(id, components)
    .map((key) => coverageInclusionPath(key))
    .filter((path) => stagedFields.at([path]).length > 0);
  if (staged.length === 0) {
    return [];
  }
  const directions = new Set(
    stagedFields.at(staged).map((row) => `${row.fromLabel}→${row.toLabel}`)
  );
  return directions.size === 1 ? [staged[0]] : [];
}

/**
 * The referral-strategy dropdown in the row's own actions menu.
 *
 * Row-eligible in `rowScope` and staged like anything else, and it was missing
 * from the list above — so an underwriter could change it, watch the rate move,
 * and find no tint, no struck premium, no Proposed figure and nothing to accept
 * or reject. Anything `rowScope` calls a row has to be addressable here.
 */
export const coverageReferralStrategyPath = (id: string): FieldPath => [
  'coverageState',
  id,
  'referralStrategyForEdit',
];

export const coverageInclusionPath = (id: string): FieldPath => [
  'coverageState',
  id,
  'isSelected',
];

export const globalFieldPath = (field: string): FieldPath => [
  'global',
  'value',
  'props',
  field,
];

/**
 * One field on a suite part's header — its Limit, Retention, or another control
 * `ComponentHeader` renders. Addressed by the part's product type, which is the
 * key `componentGlobals` files it under.
 *
 * Distinct from `globalFieldPath`: a part's `limit` and the policy's `limit` are
 * different fields, and a prefix that matched both would put the policy's
 * accept/reject pair on every part.
 */
export const componentGlobalFieldPath = (
  productType: string,
  field: string
): FieldPath => ['componentGlobals', productType, 'value', 'props', field];

/**
 * Every staged prop on one rate modifier.
 *
 * For the controls that write more than one field, or whose field depends on
 * the modifier's type (`ModifierInputFactory`). Naming the field would mean
 * re-deriving that choice out here, and a wrong guess renders as no marker at
 * all.
 */
export const modifierPath = (id: string): FieldPath => [
  'ratingOptions',
  id,
  'value',
  'props',
];

/** A rate modifier's Included/Excluded checkbox. */
export const modifierInclusionPath = (id: string): FieldPath => [
  'ratingOptions',
  id,
  'isSelected',
];

/** The referral-strategy dropdown in a rate modifier's actions menu. */
export const modifierReferralStrategyPath = (id: string): FieldPath => [
  'ratingOptions',
  id,
  'referralStrategyForEdit',
];

/**
 * Everything one rate-modifier row can stage a change at: its props, its
 * inclusion and its referral strategy. The row's one accept/reject pair asks through this, so anything
 * `rowScope` calls a modifier row has to be in it.
 */
export const modifierRowPaths = (id: string): readonly FieldPath[] => [
  modifierPath(id),
  modifierInclusionPath(id),
  modifierReferralStrategyPath(id),
];

export const modifierFieldPath = (id: string, field: string): FieldPath => [
  'ratingOptions',
  id,
  'value',
  'props',
  field,
];

/**
 * A rate-modifier category's On/Off switch.
 *
 * Addressed by the category's `key`, not its `id` — `ModifierCategoryToggle`
 * files its customization under `key`, so that is what the path segment holds.
 */
export const categoryCustomizationPath = (key: string): FieldPath => [
  'categoryCustomizations',
  'rateModifierCategoriesCustomizations',
  key,
];

export const raterOptionPath = (id: string): FieldPath => [
  'raterOptions',
  id,
  'value',
];
